import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Link } from 'react-router-dom';
import {
  JOB_STATUS_LABELS,
  JOB_STATUS_TONE,
  type JobStatus,
} from '@fsm/shared';
import { Icon, type IconName } from './icons';

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// --- buttons ----------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-white border-accent shadow-[0_2px_3px_rgb(23_76_62/0.13)] hover:bg-accent-2 hover:border-accent-2 disabled:bg-ink-4 disabled:border-ink-4 disabled:shadow-none',
  secondary:
    'bg-surface text-ink border-line-2 hover:bg-raised hover:border-accent-line hover:shadow-[0_2px_4px_rgb(22_64_45/0.04)]',
  ghost: 'bg-transparent text-ink-3 border-transparent hover:bg-raised hover:text-ink',
  danger: 'bg-surface text-danger border-line-2 hover:bg-danger-soft hover:border-danger',
};

/*
 * Heights come from the fingertip, not the label: 40px is the comfortable
 * target a dispatcher hits without looking, and `sm` at 32px is the smallest
 * that still reads as a control rather than a link.
 */
const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-2.5 text-[12px] gap-1.5 rounded-[6px]',
  md: 'h-[42px] px-3.5 text-[14px] gap-2 rounded-[7px]',
  lg: 'h-12 px-5 text-[15px] gap-2 rounded-[8px]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** A glyph before the label. Every action worth a button has one. */
  icon?: IconName;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center border font-semibold whitespace-nowrap',
        'transition-[background-color,border-color,box-shadow,transform] duration-100 select-none',
        'active:translate-y-px',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        'disabled:cursor-not-allowed disabled:opacity-60',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Spinner className="size-3.5" />
      ) : (
        icon && <Icon name={icon} size={size === 'sm' ? 14 : 16} />
      )}
      {children}
    </button>
  );
});

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cx('animate-spin size-4', className)} viewBox="0 0 16 16" fill="none" aria-hidden>
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2.5" />
      <path
        d="M14.5 8A6.5 6.5 0 0 0 8 1.5"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// --- form controls ----------------------------------------------------------

const FIELD_BASE =
  'w-full rounded-[7px] border border-line-2 bg-surface px-3 text-[13px] text-ink placeholder:text-ink-4 ' +
  'transition-colors focus:border-accent focus:outline-2 focus:outline-offset-0 focus:outline-accent/20 ' +
  'disabled:bg-raised disabled:text-ink-3';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cx(FIELD_BASE, 'h-10', className)} {...rest} />;
  },
);

/**
 * A search box.
 *
 * Four list screens had their own, and three of them had drifted on width,
 * placeholder and whether the magnifier was inside or beside the field. The
 * padding is inline rather than a `pl-9` class because which of two padding
 * utilities wins is stylesheet order, not class order — that has already cost
 * an afternoon once.
 */
export function SearchField({
  value,
  onChange,
  placeholder,
  className,
  label = 'Search',
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  className?: string;
  label?: string;
}) {
  return (
    <div className={cx('relative', className)}>
      <Icon
        name="search"
        size={15}
        className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-4"
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
        style={{ paddingLeft: '2.25rem' }}
      />
    </div>
  );
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={cx(FIELD_BASE, 'py-2 leading-relaxed', className)} {...rest} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select ref={ref} className={cx(FIELD_BASE, 'h-10 pr-8 appearance-none bg-no-repeat', className)}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='none' stroke='%238b948e' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' d='M3 4.5 6 7.5 9 4.5'/%3E%3C/svg%3E\")",
          backgroundPosition: 'right 10px center',
        }}
        {...rest}
      >
        {children}
      </select>
    );
  },
);

export function Field({
  label,
  hint,
  error,
  required,
  children,
  className,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cx('block', className)}>
      <span className="mb-1.5 flex items-baseline gap-1.5 text-[12px] font-medium text-ink-2">
        {label}
        {required && <span className="text-danger">*</span>}
        {hint && <span className="font-normal text-ink-4">{hint}</span>}
      </span>
      {children}
      {error && <span className="mt-1 block text-[12px] text-danger">{error}</span>}
    </label>
  );
}

// --- surfaces ---------------------------------------------------------------

/**
 * A card.
 *
 * The anatomy is fixed, because it is what makes twenty panels across ten
 * screens read as one application: a heading line carrying an icon, the title,
 * an optional count, and one action on the right; the body; and — when the
 * card adds up to a figure — a tinted band across its foot.
 *
 * `divided` puts a rule under the heading. That is for cards whose body is a
 * table: the rule and the table's own header rule then read as one edge
 * instead of two lines a few pixels apart.
 */
export function Card({
  title,
  icon,
  count,
  action,
  children,
  band,
  footLink,
  divided,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  /** A glyph before the title — the card's subject, not decoration. */
  icon?: IconName;
  /** A count chip after the title: how many jobs, line items, messages. */
  count?: number;
  action?: ReactNode;
  children: ReactNode;
  /** The tinted strip across the card's foot — see `SummaryBand`. */
  band?: ReactNode;
  /** A full-width link across the bottom: "View customer profile ›". */
  footLink?: { to: string; label: string };
  divided?: boolean;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section
      className={cx(
        'overflow-hidden rounded-card border border-line bg-surface shadow-[0_2px_3px_rgb(37_59_45/0.012)]',
        className,
      )}
    >
      {(title || action) && (
        <header
          className={cx(
            'flex items-center justify-between gap-3 px-[21px] py-[17px]',
            divided && 'border-b border-line',
          )}
        >
          {typeof title === 'string' ? (
            <h2 className="flex min-w-0 items-center gap-2 font-display text-[16px] font-bold tracking-[-0.015em] text-ink">
              {icon && <Icon name={icon} size={18} className="text-ink-4" />}
              <span className="truncate">{title}</span>
              {count !== undefined && <CountChip value={count} />}
            </h2>
          ) : (
            title
          )}
          {action}
        </header>
      )}

      <div className={cx('px-[21px] pb-[21px]', !title && 'pt-[21px]', bodyClassName)}>
        {children}
      </div>

      {band}

      {footLink && (
        <Link
          to={footLink.to}
          className="flex items-center justify-between border-t border-line px-[21px] py-3.5 text-[13px] font-semibold text-accent transition-colors hover:bg-raised"
        >
          {footLink.label}
          <Icon name="chevron" size={14} />
        </Link>
      )}
    </section>
  );
}

/** The small grey number beside a card title. */
export function CountChip({ value }: { value: number }) {
  return (
    <span className="min-w-[20px] shrink-0 rounded-[5px] bg-raised px-1.5 text-center text-[12px] font-medium text-ink-4">
      {value}
    </span>
  );
}

/**
 * The band across the foot of a card that adds up to money.
 *
 * Green when the figure is settled, warm when something is still owed. It is a
 * band rather than a last table row because it answers a different question
 * from the rows above it — those say what was charged, this says where you
 * stand — and burying that in a table is how people end up adding the column
 * up themselves.
 */
export function SummaryBand({
  label,
  caption,
  amount,
  amountLabel,
  tone = 'good',
  icon,
}: {
  label: string;
  caption?: ReactNode;
  amount: string;
  /** The small word above the figure: "Total". */
  amountLabel?: string;
  tone?: 'good' | 'owed';
  icon?: IconName;
}) {
  const good = tone === 'good';
  return (
    <div
      className={cx(
        'flex items-center justify-between gap-5 border-t px-[25px] py-5',
        good ? 'border-good/15 bg-good-soft' : 'border-warn/15 bg-[#fff6f2]',
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        {icon && (
          <span
            className={cx(
              'grid size-9 shrink-0 place-items-center rounded-full',
              good ? 'bg-good/12 text-good' : 'bg-danger/10 text-danger',
            )}
          >
            <Icon name={icon} size={17} />
          </span>
        )}
        <div className="min-w-0">
          <div className={cx('text-[14px] font-semibold', good ? 'text-good' : 'text-danger')}>
            {label}
          </div>
          {caption && (
            <div className={cx('mt-1 text-[12px]', good ? 'text-good/75' : 'text-danger/70')}>
              {caption}
            </div>
          )}
        </div>
      </div>

      <div className="shrink-0 text-right">
        {amountLabel && <div className="text-[12px] text-ink-4">{amountLabel}</div>}
        <div
          className={cx(
            'font-display text-[30px] leading-none font-bold tracking-[-0.03em] tnum',
            good ? 'text-good' : 'text-danger',
          )}
        >
          {amount}
        </div>
      </div>
    </div>
  );
}

/**
 * A tinted notice inside a card — a warning about test payments, a note that
 * costs stay internal. Amber by default because every one of them so far has
 * been a caveat rather than an error.
 */
export function Callout({
  title,
  children,
  tone = 'warn',
  icon = 'info',
}: {
  title?: string;
  children?: ReactNode;
  tone?: 'warn' | 'info';
  icon?: IconName;
}) {
  return (
    <div
      className={cx(
        'flex gap-2.5 rounded-[7px] border p-3',
        tone === 'warn' ? 'border-[#f0e5cd] bg-[#fff8e9] text-warn' : 'border-line bg-raised text-ink-3',
      )}
    >
      <Icon name={icon} size={16} className="mt-0.5" />
      <div className="min-w-0">
        {title && <div className="text-[12.5px] font-semibold">{title}</div>}
        {children && <div className="mt-1 text-[12.5px] leading-relaxed opacity-90">{children}</div>}
      </div>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
      <p className="font-display text-[15px] font-bold tracking-[-0.01em] text-ink">{title}</p>
      {description && <p className="max-w-sm text-[13px] text-ink-3">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

// --- badges -----------------------------------------------------------------

/*
 * Tones are named after what they mean elsewhere in the app, not after the
 * hue — `emerald` is "settled", `amber` is "needs a person". They are tuned
 * against the green accent rather than taken from a stock ramp, so a chip
 * never out-shouts the one primary button on the screen.
 */
const TONE_CLASSES: Record<string, string> = {
  slate: 'bg-tone-slate-soft text-tone-slate ring-tone-slate/12',
  blue: 'bg-tone-blue-soft text-tone-blue ring-tone-blue/18',
  amber: 'bg-warn-soft text-warn ring-warn/20',
  green: 'bg-accent-soft text-accent ring-accent/18',
  violet: 'bg-tone-violet-soft text-tone-violet ring-tone-violet/18',
  emerald: 'bg-good-soft text-good ring-good/18',
  red: 'bg-danger-soft text-danger ring-danger/18',
};

export function Badge({
  tone = 'slate',
  children,
  className,
}: {
  tone?: keyof typeof TONE_CLASSES;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 rounded-[6px] px-2 py-[3px] text-[12px] font-semibold ring-1 ring-inset whitespace-nowrap',
        TONE_CLASSES[tone] ?? TONE_CLASSES['slate'],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function StatusBadge({ status, className }: { status: JobStatus; className?: string }) {
  return (
    <Badge tone={JOB_STATUS_TONE[status]} className={className}>
      {JOB_STATUS_LABELS[status]}
    </Badge>
  );
}

// --- modal ------------------------------------------------------------------

/** Open modals, innermost last. See the Escape handling in `Modal`. */
const modalStack: symbol[] = [];

export function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  children,
  width = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  footer?: ReactNode;
  children: ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const ref = useRef<HTMLDivElement>(null);
  const id = useRef<symbol>(undefined as unknown as symbol);
  if (!id.current) id.current = Symbol('modal');

  /*
   * `onClose` through a ref, and the effect below depends on `open` alone.
   *
   * This is not tidiness. Callers pass a function declared in their own body
   * — `onClose={close}` — which is a new reference on every render. With
   * `onClose` in the dependency array, every keystroke in any field re-ran
   * this effect, and re-running it calls `ref.current.focus()`, which moves
   * focus off the input and onto the dialog. The symptom was a form that
   * accepted exactly one character at a time: type, lose the caret, click
   * back in, type one more.
   *
   * A ref read at call time keeps the Escape handler current without making
   * the effect's identity depend on the caller's render.
   */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    /*
     * Modals stack — "New customer" opens over "New job" — and every open one
     * has its own Escape listener. Without this, one press closed the whole
     * pile and threw away the half-booked job underneath. Only the topmost
     * answers; the rest wait their turn.
     */
    const self = id.current;
    modalStack.push(self);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (modalStack[modalStack.length - 1] !== self) return;
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    /*
     * Focus the panel so screen readers and Tab both start inside it — but
     * not over the top of a field that asked for focus itself. A form whose
     * first input carries `autoFocus` is saying where the caret belongs, and
     * React applies that during the commit, before this effect runs.
     */
    const panel = ref.current;
    if (panel && !panel.contains(document.activeElement)) panel.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      const at = modalStack.lastIndexOf(self);
      if (at >= 0) modalStack.splice(at, 1);
    };
  }, [open]);

  if (!open) return null;

  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div
        className="fixed inset-0 bg-ink/30 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cx(
          'relative w-full rounded-card border border-line bg-surface shadow-[0_24px_60px_-20px_rgb(23_43_34/0.35)] outline-none',
          widths[width],
        )}
      >
        <header className="border-b border-line px-5 py-4">
          <h2 className="font-display text-[17px] font-extrabold tracking-[-0.02em] text-ink">
            {title}
          </h2>
          {description && <p className="mt-0.5 text-[13px] text-ink-3">{description}</p>}
        </header>
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <footer className="flex justify-end gap-2 rounded-b-card border-t border-line bg-raised px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

// --- tables -----------------------------------------------------------------

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('overflow-x-auto', className)}>
      <table className="w-full border-collapse text-[14px] tabular-nums">{children}</table>
    </div>
  );
}

export function Th({
  children,
  className,
  align = 'left',
}: {
  children?: ReactNode;
  className?: string;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      className={cx(
        'border-y border-line bg-raised px-4 py-2.5 text-[12px] font-medium text-ink-3 first:pl-[21px] last:pr-[21px]',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        align === 'left' && 'text-left',
        className,
      )}
    >
      {children}
    </th>
  );
}

/**
 * Sorting a table by clicking its headings.
 *
 * The state and the comparison live together here so that three list screens
 * cannot end up with three subtly different ideas of where blanks go or how
 * two equal rows are ordered. What each column sorts *on* is the page's
 * business — it passes a function per column — but everything after that is
 * written once.
 */
export type SortDirection = 'asc' | 'desc';

export interface TableSort<K extends string> {
  key: K;
  direction: SortDirection;
  /** Clicking the active column flips it; clicking another starts it ascending. */
  toggle: (key: K) => void;
}

export function useTableSort<K extends string>(
  initialKey: K,
  initialDirection: SortDirection = 'asc',
): TableSort<K> {
  const [key, setKey] = useState<K>(initialKey);
  const [direction, setDirection] = useState<SortDirection>(initialDirection);

  return {
    key,
    direction,
    toggle: (next: K) => {
      if (next === key) {
        setDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setKey(next);
        setDirection('asc');
      }
    },
  };
}

/** What a column sorts on: a number, a string, or null for "no value". */
export type SortValue = string | number | null | undefined;

/**
 * Sort a copy of `rows`.
 *
 * Rows with no value always sink to the bottom, in both directions. A customer
 * who has never been billed belongs at the end of a list sorted by balance,
 * not at the top of the descending one pretending to owe the most.
 */
export function sortRows<T, K extends string>(
  rows: T[],
  sort: TableSort<K>,
  valueOf: (row: T, key: K) => SortValue,
): T[] {
  const sign = sort.direction === 'asc' ? 1 : -1;

  return [...rows].sort((a, b) => {
    const left = valueOf(a, sort.key);
    const right = valueOf(b, sort.key);

    const leftEmpty = left === null || left === undefined || left === '';
    const rightEmpty = right === null || right === undefined || right === '';
    if (leftEmpty && rightEmpty) return 0;
    if (leftEmpty) return 1;
    if (rightEmpty) return -1;

    if (typeof left === 'number' && typeof right === 'number') {
      return (left - right) * sign;
    }
    // Locale-aware and numeric-aware, so "#9" sorts before "#10".
    return String(left).localeCompare(String(right), undefined, { numeric: true }) * sign;
  });
}

/** A heading that sorts. Same padding and weight as `Th`, plus an arrow. */
export function SortTh<K extends string>({
  sortKey,
  sort,
  children,
  align = 'left',
  className,
}: {
  sortKey: K;
  sort: TableSort<K>;
  children?: ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
}) {
  const active = sort.key === sortKey;

  return (
    <th
      className={cx(
        /*
         * The gutter goes on the cell, not inside the button: a nested
         * arbitrary selector for the same 21px is a rule only this file
         * understands, and the first and last columns must line up with the
         * card's own padding.
         */
        'border-y border-line bg-raised p-0 text-[12px] font-medium first:pl-[5px] last:pr-[5px]',
        className,
      )}
      aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => sort.toggle(sortKey)}
        className={cx(
          'flex w-full items-center gap-1 px-4 py-2.5 transition-colors hover:text-ink',
          active ? 'font-semibold text-ink' : 'text-ink-3',
          align === 'right' && 'justify-end',
          align === 'center' && 'justify-center',
        )}
      >
        {children}
        {/*
         * The arrow only appears on the sorted column. An arrow on every
         * heading turns a table into a row of chevrons and stops saying
         * which one is actually doing the work.
         */}
        <span className={cx('text-[9px] leading-none', active ? 'opacity-100' : 'opacity-0')}>
          {sort.direction === 'asc' ? '▲' : '▼'}
        </span>
      </button>
    </th>
  );
}

export function Td({
  children,
  className,
  align = 'left',
}: {
  children?: ReactNode;
  className?: string;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <td
      className={cx(
        'border-b border-line px-4 py-[17px] align-middle text-ink-2 first:pl-[21px] last:pr-[21px]',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className,
      )}
    >
      {children}
    </td>
  );
}

// --- misc -------------------------------------------------------------------

export function Avatar({
  name,
  color,
  textColor,
  size = 28,
  radius = '50%',
  className,
}: {
  name: string;
  color?: string | null;
  /** Defaults to white; pass one when the ground is pale. */
  textColor?: string;
  size?: number;
  /** A person is a circle; a record's tile in a page heading is a rounded square. */
  radius?: string;
  className?: string;
}) {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
  return (
    <span
      className={cx('inline-grid shrink-0 place-items-center font-semibold', className)}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: color ?? '#6b7686',
        color: textColor ?? '#ffffff',
        fontSize: Math.round(size * 0.4),
      }}
      title={name}
    >
      {letters}
    </span>
  );
}

export function Toast({
  message,
  tone = 'info',
  onDismiss,
}: {
  message: ReactNode;
  tone?: 'info' | 'error' | 'success';
  onDismiss: () => void;
}) {
  useEffect(() => {
    const t = setTimeout(onDismiss, tone === 'error' ? 8000 : 4000);
    return () => clearTimeout(t);
  }, [onDismiss, tone]);

  const tones = {
    info: 'bg-ink text-white',
    error: 'bg-danger text-white',
    success: 'bg-good text-white',
  };

  return (
    <div
      role="status"
      className={cx(
        'fixed bottom-5 left-1/2 z-[60] flex -translate-x-1/2 items-center gap-3',
        'rounded-[9px] px-4 py-2.5 text-[13px] font-medium shadow-[0_16px_40px_-12px_rgb(23_43_34/0.45)]',
        tones[tone],
      )}
    >
      {message}
      <button onClick={onDismiss} className="opacity-60 hover:opacity-100" aria-label="Dismiss">
        ✕
      </button>
    </div>
  );
}

/**
 * The filter strip under a list page's title.
 *
 * Jobs, invoices, estimates and calls each had their own copy of this — four
 * hand-written tab rows that had already drifted on padding and on what the
 * selected one looked like. One component, four callers.
 *
 * It is a segmented control rather than underlined tabs because the heading no
 * longer sits on a white band: an underline needs a rule to sit on, and on the
 * open canvas it read as a stray mark under one word.
 */
export function FilterTabs<T extends string>({
  value,
  onChange,
  options,
  className,
}: {
  value: T;
  onChange: (next: T) => void;
  options: Array<{ value: T; label: string }>;
  className?: string;
}) {
  return (
    <nav
      className={cx(
        'inline-flex max-w-full gap-0.5 overflow-x-auto rounded-[9px] border border-line bg-surface p-1',
        className,
      )}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={value === option.value}
          className={cx(
            'rounded-[6px] px-3 py-1.5 text-[12.5px] whitespace-nowrap transition-colors',
            value === option.value
              ? 'bg-accent-soft font-semibold text-accent'
              : 'font-medium text-ink-3 hover:bg-raised hover:text-ink',
          )}
        >
          {option.label}
        </button>
      ))}
    </nav>
  );
}

/**
 * The heading every screen opens with.
 *
 * Eyebrow, title, one line of grey fact, actions on the right — and nothing
 * else. It sits on the canvas rather than in a white band of its own: the
 * white belongs to the cards, and giving the title its own panel made every
 * list page open with two stacked headers, one of which said nothing.
 */
export function PageHeader({
  title,
  subtitle,
  actions,
  tabs,
  eyebrow,
  avatar,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  tabs?: ReactNode;
  /** The small caps line over the title: "CUSTOMER PROFILE". */
  eyebrow?: ReactNode;
  /** A square tile to the left of the title — used for a person. */
  avatar?: ReactNode;
}) {
  return (
    <header className="shrink-0 bg-canvas px-[30px] pt-7">
      <div className="mx-auto flex max-w-[1860px] items-center justify-between gap-5 pb-7">
        <div className="flex min-w-0 items-center gap-[19px]">
          {avatar}
          <div className="min-w-0">
            {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
            <h1 className="truncate font-display text-[32px] leading-[1.25] font-bold tracking-[-0.035em] text-ink">
              {title}
            </h1>
            {subtitle && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[13px] text-ink-3">
                {subtitle}
              </div>
            )}
          </div>
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center justify-end gap-2.5">{actions}</div>}
      </div>
      {tabs && <div className="pb-6">{tabs}</div>}
    </header>
  );
}

/** The caps line above a title. One implementation, one letter-spacing. */
export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="mb-1.5 flex items-center gap-2 text-[12px] font-semibold tracking-[0.15em] text-ink-4 uppercase">
      {children}
    </div>
  );
}

/**
 * The separator between two facts on one grey line: "Desmond Abara · Job #1025".
 * A component rather than a literal so the colour and the spacing are decided
 * once — a middot typed inline picks up the weight of whatever is beside it.
 */
export function Dot() {
  return (
    <span className="px-0.5 text-line-2" aria-hidden>
      ·
    </span>
  );
}
